import {
	ItemSource,
	dismissItemError,
	getCartItemErrors,
	getCartItems,
	getItemPrices,
	getPriceForItem,
	reportItemError,
	setCartItems,
} from '../reducers/checkout.reducer';
import { getMyProducts, hasInitialized, updateMyProducts } from '../reducers/persisted-list.reducer';
import { isPrintfileAvailable } from '../utils/checkout-utils';
import {
	CartItemAntivirusInfectedError,
	CartItemAntivirusScanFailedError,
	CartItemError,
	CartItemNotAvailableError,
	CartItemOptionsInvalidError,
	CartItemPriceChangeError,
	CartItemPricingError,
	CartItemPrintfileMissingError,
	CartItemStockLevelTooLowError,
	CartItemUnsupportedPrintfileError,
} from './cart-item-errors';
import { PrintfileState, PrintfileValidatorService } from './printfile-validator.service';
import { StorePersistService } from './store-persist.service';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { getProductsByIdList as fetchProductsByIdList } from '@app/api/action/Product';
import { RootReducer, Store } from '@app/app.reducers';
import { ConfirmDialogV2Component } from '@app/dialog/components/confirm-dialog-v2/confirm-dialog-v2.component';
import { AntivirusStatus, PreflightError } from '@app/file-api/file-api.constants';
import {
	getAllProductsById,
	isError as isErrorProducts,
	isLoading as isLoadingProducts,
} from '@app/product/reducers/product.reducer';
import { getProductQuantity } from '@app/product/utils/product-utils';
import { getStockAmount } from '@app/product/utils/stock-product';
import { getFileByKey } from '@app/shared/reducers/file.reducer';
import { popErrorToast } from '@app/shared/reducers/toast.reducer';
import { NewRelic } from '@app/shared/services/newrelic.service';
import {
	firstTruthy,
	getDebug,
	isObjectContained,
	isPlainObjectEqual,
	mapToRx,
	omit,
	pick,
} from '@app/shared/utils/util';
import { getUserPermissions } from '@app/user/reducers/user.reducer';
import {
	Pending,
	foldSuccess,
	isFailure,
	isResolved,
	pluckSuccessData,
} from '@granodigital/grano-remote-data';
import {
	Subject,
	Subscription,
	first,
	firstValueFrom,
	fromEvent,
	map,
	merge,
	share,
	switchMap,
	throttleTime,
} from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

/**
 * How often to perform a full item list refresh upon a change to any item.
 * This is needed to check the stock levels, especially in cases in which there are multiple items that share the stock.
 * However, we don't really need to perform price checks in this case.
 */
export const PRODUCT_REFRESH_THROTTLE = 1000;

type RefreshCartProductsKeys<T> = {
	[K in keyof T]: Required<T>[K] extends object
		? Required<T>[K] extends unknown[]
			? K & string
			: [K & string, (keyof Required<T>[K])[]]
		: K & string;
}[keyof T][];

/** Keys to update when refreshing product data */
const refreshCartProductsKeys = [
	'stock_level',
	[
		'metadata',
		[
			'product_group_id_v2',
			'sonet_number',
			'cost_center',
			'order_status',
			'work_queue',
			'additional_information',
			'internal_instructions',
			'distribution_information',
			'workflow',
			'automation_producttype',
			'production_workflow_definition',
			'production_time',
			'product_id',
			'unit',
		],
	],
	[
		'locale',
		[
			'product_name',
			'product_name_sub_text',
			'product_description',
			'technical_description',
			'internal_information',
			'shipping_info',
		],
	],
	['images', ['master']], // Thumbnail for cart
	[
		'properties',
		[
			'imageKeys',
			'printfiles',
			'additionalFiles',
			'selectableOptions',
			// Needed for anonymous mode to work properly.
			'textFields',
		],
	],
	'slug',
	'product_properties',
	// Needed for anonymous mode to work properly.
	'type',
	'categories',
] satisfies RefreshCartProductsKeys<api.ProductDto>;

// Using the DTO directly causes index signature errors.
type CartItemProduct = Pick<api.CartItemProductDto, keyof api.CartItemProductDto>;

/** These product options are never updated. */
const selectedOptionsNotUpdated = [
	'order_quantity',
	'quantity',
	'is_premedia_fix_requested',
	'premedia_fix_request_message',
	'scope',
] as const;

export interface CartValidationResult {
	blockers: CartItemError[];
	warnings: CartItemError[];
	isCartEmpty: boolean;
	/** True if all cart items have been processed.  */
	areAllItemsProcessed: boolean;
	/**
	 * True if all cart items are valid. Warnings are allowed.
	 * False if any printfiles are still being processed or they failed any checks.
	 * @todo ED-2606 Remove this and make validation errors to be included in the CartItemErrors.
	 */
	areAllPrintfilesValid: boolean;
}
/**
 * Service for refreshing and validating shopping cart products and their data.
 */
@Injectable({ providedIn: 'root' })
export class ShoppingCartRefreshService {
	private readonly debug = getDebug('ShoppingCartRefreshService');
	private readonly refreshSentinel = new Subject<ItemSource>();
	private readonly refresh$: Observable<void>;
	private subscription?: Subscription;

	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly newRelic = inject(NewRelic);
	private readonly router = inject(Router);
	private readonly dialog = inject(MatDialog);
	private readonly printfileValidator = inject(PrintfileValidatorService);
	private readonly persist = inject(StorePersistService);

	constructor() {
		this.refresh$ = this.startProductDataRefresh$();
	}

	/**
	 * @returns Error map of the cart items.
	 */
	async validateCart(): Promise<CartValidationResult> {
		const cartItems = await firstValueFrom(this.store.select(getCartItems));
		const itemErrors = await firstValueFrom(this.store.select(getCartItemErrors));

		const validation: CartValidationResult = {
			blockers: [],
			warnings: [],
			areAllItemsProcessed: true,
			areAllPrintfilesValid: true,
			isCartEmpty: cartItems.length === 0,
		};

		// Validate stock levels for all cart items and report errors
		this.validateStockLevels(cartItems);

		for (const item of cartItems) {
			if (this.printfileValidator.getCartItemFileState(item) === PrintfileState.Processing) {
				validation.areAllItemsProcessed = false;
			}
			if (!this.printfileValidator.hasValidPrintfile(item, true)) {
				validation.areAllPrintfilesValid = false;
			}

			for (const error of itemErrors.values()) {
				if (error.itemId === item.id) {
					(error?.isBlocker ? validation.blockers : validation.warnings).push(error);
				}
			}
		}

		return validation;
	}

	/**
	 * Validates the cart items and asks the user to proceed if there are warnings.
	 * @returns True if the cart is valid and the order can be created, false otherwise.
	 */
	async validateCartBeforeOrder(
		options: {
			shouldCheckPermissions?: boolean;
			shouldTriggerRefresh?: boolean;
			shouldIgnoreWarnings?: boolean;
		} = {},
	): Promise<boolean> {
		const {
			shouldCheckPermissions = true,
			shouldTriggerRefresh = true,
			shouldIgnoreWarnings = false,
		} = options;
		if (shouldCheckPermissions) {
			const userPermissions = await firstValueFrom(
				this.store.select(getUserPermissions).pipe(firstTruthy),
			);
			if (!userPermissions.allow_checkout) {
				this.store.dispatch(
					popErrorToast({
						title: $localize`:@@ToastNoCheckoutPermission:No permission to checkout`,
					}),
				);
				return false;
			}
		}

		if (shouldTriggerRefresh) {
			this.debug('Refreshing product data before validating cart items');
			await this.triggerRefresh(ItemSource.ShoppingCart);
		}

		const { blockers, warnings, isCartEmpty, areAllItemsProcessed, areAllPrintfilesValid } =
			await this.validateCart();

		if (isCartEmpty) {
			this.store.dispatch(
				popErrorToast({
					title: $localize`:@@ToastOrderEmptyCart:Your cart is empty. Please add items to your cart before continuing.`,
				}),
			);
			return false;
		}

		if (!areAllItemsProcessed) {
			this.store.dispatch(
				popErrorToast({
					// eslint-disable-next-line max-len
					title: $localize`:@@ToastOrderItemsUnprocessed:Some items in your cart are still being processed. Please wait a moment and try again.`,
				}),
			);
			return false;
		}

		if (blockers.length > 0 || areAllPrintfilesValid === false) {
			// Check whether cart contains any blocking errors.
			// If there are errors that require user intervention, show a toast and prevent order creation.
			this.store.dispatch(
				popErrorToast({
					title: $localize`:@@ToastOrderItemErrors:Some items in the cart have errors. Please fix them before continuing.`,
				}),
			);
			this.debug('Blocking errors found', { blockers, areAllPrintfilesValid });
			this.newRelic.noticeError(new Error('Blockers in cart'), {
				blockers: blockers.map((e) => e.key).join('|'),
			});
			return false;
		}

		if (shouldIgnoreWarnings || warnings.length === 0) return true;

		// If there are no blocking errors, ask the user if they want to proceed
		return ConfirmDialogV2Component.open(this.dialog, {
			title: $localize`:@@ConfirmOrderWithWarningsTitle:Some items in the cart have warnings`,
			description: $localize`:@@ConfirmOrderWithWarningsBody:Do you want to place the order?`,
			acceptLabel: $localize`:@@ConfirmOrderWithWarningsAccept:Place order`,
			rejectLabel: $localize`:@@ConfirmOrderWithWarningsReject:Let me check first`,
		});
	}

	/** Triggers a manual refresh of the shopping cart products. */
	async triggerRefresh(source = this.determineItemSource()): Promise<void> {
		// Start the refresh process on the first trigger and keep it running forever.
		if (!this.subscription) {
			this.debug('Initializing refresh subscription');
			this.subscription = this.refresh$.subscribe();
		}
		// Only trigger refresh if user is in checkout
		if (!source || !this.router.url.startsWith('/checkout')) {
			this.debug('Skipping refresh', { source, url: this.router.url });
			return;
		}
		const refreshPromise = firstValueFrom(this.refresh$);
		this.refreshSentinel.next(source);
		return refreshPromise;
	}

	/**
	 * Starts the product data refresh process.
	 * This method returns an Observable that emits void values at a specified interval.
	 * The refresh is triggered only when the document is not hidden.
	 * If an error occurs during the refresh, it is caught and logged using New Relic.
	 */
	private startProductDataRefresh$(): Observable<void> {
		// Wait for the state to initialize before starting the refresh.
		return this.store.select(hasInitialized).pipe(
			firstTruthy,
			switchMap(() =>
				merge(
					fromEvent(document, 'visibilitychange').pipe(mapToRx(['VisibilityChange'] as const)),
					this.refreshSentinel.pipe(map((source) => ['RefreshSentinel', source] as const)),
				),
			),
			// Make sure multiple changes are batched.
			throttleTime(PRODUCT_REFRESH_THROTTLE, undefined, { leading: true, trailing: true }),
			switchMap(async ([trigger, source]) => {
				try {
					// Skip refresh when the document is hidden.
					if (document.hidden) return;
					const itemSource = source ?? this.determineItemSource();
					// Skip refresh when the item source is unknown.
					if (!itemSource) return;
					this.debug('Triggered refresh', trigger, itemSource);
					await this.refreshProducts(itemSource);
				} catch (err) {
					// Log any errors that occur during the refresh, but do not stop the refresh process.
					this.newRelic.noticeError(err);
				}
			}),
			share(),
		);
	}

	/** Determines the source of the items based on the current route. */
	private determineItemSource(): ItemSource | undefined {
		const currentUrl = this.router.url;
		if (currentUrl.includes('/checkout/my-products')) return ItemSource.MyProducts;
		if (currentUrl.startsWith('/checkout')) return ItemSource.ShoppingCart;
	}

	/** Updates cart products with data fetched from api. */
	private async refreshProducts(itemSource: ItemSource = ItemSource.ShoppingCart): Promise<void> {
		const productIds = new Set<number>();
		const items = await firstValueFrom(
			itemSource === ItemSource.ShoppingCart
				? this.store.select(getCartItems)
				: this.store.select(getMyProducts).pipe(
						pluckSuccessData,
						map((list) => list.products),
					),
		);
		this.debug('Refreshing product data', itemSource, items);
		// No items in cart, skip.
		if (items.length === 0) return;
		// Fetch updated product data
		for (const { product } of items) productIds.add(product.id);
		this.debug('Fetching products', productIds);
		this.store.dispatch(fetchProductsByIdList([...productIds].join(',')));
		this.debug('Fetching prices');
		const itemPrices = new Map(
			await Promise.all(
				items.map(async (item) => {
					// Get current price if available.
					const currentPrice = await firstValueFrom(
						this.store
							.select(getItemPrices)
							.pipe(map((prices) => foldSuccess(prices.get(item.id) ?? new Pending()))),
					);
					// Fetch new price.
					this.store.dispatch(getPriceForItem(item, { body: item.options.selectedOptions }));
					// Wait for the price to be fetched.
					const price = await firstValueFrom(
						this.store.select(getItemPrices).pipe(
							map((prices) => prices.get(item.id) ?? new Pending()),
							first(isResolved),
						),
					);
					// Check if there was an error.
					if (isFailure(price)) {
						this.store.dispatch(
							reportItemError(new CartItemPricingError(item.id, itemSource, price.error?.body?.trace)),
						);
						return [item.id, {}] as const;
					}
					this.store.dispatch(dismissItemError(new CartItemPricingError(item.id, itemSource)));
					return [item.id, { currentPrice, newPrice: price.data }] as const;
				}),
			),
		);
		// Wait for product data to be fetched.
		await firstValueFrom(this.store.select(isLoadingProducts).pipe(first((isLoading) => !isLoading)));
		// Check if there was an error.
		if (await firstValueFrom(this.store.select(isErrorProducts)))
			throw new Error('Failed to fetch products');

		const productsById = await firstValueFrom(this.store.select(getAllProductsById));

		this.debug('Fetched products', productsById);
		let didUpdate = false;

		const updatedItems = items?.map((item) => {
			const fetchedProduct = productsById.get(item.product.id);
			if (fetchedProduct) {
				// Mark item as available if it was previously marked as not available.
				// E.g. if network request failed previously.
				this.store.dispatch(dismissItemError(new CartItemNotAvailableError(item.id, itemSource)));
			} else {
				// No fetched product found.
				this.debug('Product not fetched', item.product.sku);
				this.store.dispatch(reportItemError(new CartItemNotAvailableError(item.id, itemSource)));
				return item;
			}

			// Check if the printfile is available.
			if (!isPrintfileAvailable(item)) {
				this.store.dispatch(reportItemError(new CartItemPrintfileMissingError(item.id, itemSource)));
				return item;
			}

			const updatedItem = this.refreshItem(
				item,
				itemSource,
				fetchedProduct as CartItemProduct,
				itemPrices.get(item.id)!,
			);

			// Mapper returned undefined, no update needed.
			if (!updatedItem) {
				this.debug('No update needed for', item.product.sku);
				return item;
			}

			didUpdate = true;
			this.debug('Updated product', item.product.sku, updatedItem);
			return { ...updatedItem };
		});

		if (this.persist.getFlag('enableNewFileInfra')) {
			await this.processFileErrors(items, itemSource);
		}

		// Only update state if something changed.
		if (didUpdate) {
			this.debug('Updating items', updatedItems);
			this.store.dispatch(
				itemSource === ItemSource.ShoppingCart
					? setCartItems(updatedItems)
					: updateMyProducts(updatedItems),
			);
		}

		// Clear resolved stock errors after product refresh
		if (itemSource === ItemSource.ShoppingCart) {
			this.validateStockLevels(updatedItems);
		}
	}

	/** Refreshes the cart item with the fetched product data. */
	private refreshItem(
		currentItem: api.CartItemWithProductDto,
		itemSource: ItemSource,
		fetchedProduct: CartItemProduct,
		{ currentPrice, newPrice }: { currentPrice?: api.ProductPriceDto; newPrice?: api.ProductPriceDto },
	): api.CartItemWithProductDto | undefined {
		const updatedItem: api.CartItemWithProductDto = {
			...this.mergeSelectedOptions(currentItem, itemSource, fetchedProduct),
			product: this.mergeFetched(currentItem.product, fetchedProduct, refreshCartProductsKeys),
			// We need to add price data to my products items. Rerunning the old infra preflight
			// expects the size information, which is only available in the price responses.
			// TODO (ED-2480): See if this is needed with the new infra.
			price: newPrice ?? currentPrice ?? currentItem.price,
		};
		this.debug('Item v. Updated', currentItem.product.sku, { currentItem, updatedItem });

		const hasPriceChanged = !isObjectContained(currentPrice, newPrice);
		if (hasPriceChanged && newPrice) {
			this.debug('Price has changed', currentPrice ?? 'none', '=>', newPrice);
			// Only notify the user if the price has changed from an existing value.
			if (currentPrice)
				this.store.dispatch(reportItemError(new CartItemPriceChangeError(currentItem.id, itemSource)));
			return updatedItem;
		}

		// Don't update if data is equal to current state.
		if (
			isPlainObjectEqual(currentItem.options.selectedOptions, updatedItem.options.selectedOptions) &&
			isObjectContained(currentItem.product, updatedItem.product)
		)
			return undefined;

		// Something did change, but the user doesn't need to be notified.
		return updatedItem;
	}

	/**
	 * Merges the fetched data into the shopping cart object based on the specified keys.
	 * @param product The original shopping cart data.
	 * @param fetchedProduct The fetched data to be merged into the shopping cart.
	 * @param keys The keys specifying which properties to merge.
	 * @returns The new merged shopping cart object.
	 */
	private mergeFetched<T extends Record<string, unknown>>(
		product: T,
		fetchedProduct: T,
		keys: RefreshCartProductsKeys<T>,
	): T {
		const result: Record<string, unknown> = {};

		for (const property of keys) {
			if (typeof property === 'string') {
				if (property in fetchedProduct) {
					result[property] = fetchedProduct[property];
				}
			} else {
				const [keyName, subKeys] = property;
				if (keyName in fetchedProduct) {
					result[keyName] = this.mergeFetched(
						product[keyName] as T,
						fetchedProduct[keyName] as T,
						subKeys as RefreshCartProductsKeys<T>,
					);
				}
			}
		}

		return { ...product, ...result };
	}

	/**
	 * Compare each field in the current product's and cart item's selectableOptions, except order quantity:
	 * 1. If a field has been removed from selectableOptions, let's remove it from selectedOptions
	 * 2. If a field has been added to selectableOptions and it only has one selectableValue, let's add it to selectedOptions
	 * 3. If a field only has one selectableValue, and it previously had only one selectableValue, and the selectableValue has changed,
	 *    let's refresh the value in selectedOptions
	 * @param item The cart item to update
	 * @param itemSource The source of the cart item
	 * @param fetchedProduct The new product data
	 */
	private mergeSelectedOptions(
		item: api.CartItemWithProductDto,
		itemSource: ItemSource,
		fetchedProduct: api.CartItemProductDto,
	): api.CartItemWithProductDto {
		const selectedOptions = omit(item.options?.selectedOptions ?? {}, selectedOptionsNotUpdated);
		// Dropdown variables.
		const selectableOptions = omit(
			(fetchedProduct.properties?.selectableOptions ?? {}) as Record<
				keyof api.OrderProductSelectedOptionsDto,
				{ selectableValues: string[] }
			>,
			selectedOptionsNotUpdated,
		) as Record<keyof typeof selectedOptions, { selectableValues: string[] }>;
		this.debug('Product properties', fetchedProduct.product_properties);
		// Custom text fields.
		const textFields = new Set(
			Object.keys((fetchedProduct.properties?.textFields ?? {}) as Record<string, unknown>),
		);
		// If a field has been removed from selectableOptions & textFields, remove it from selectedOptions.
		const updatedSelectedOptions = Object.entries(selectedOptions).filter(
			([key]) => key in selectableOptions || textFields.has(key),
		);
		// Override the value if there is only one selectable value.
		for (const [keyStr, value] of Object.entries(selectableOptions)) {
			const key = keyStr as keyof typeof selectableOptions;
			const currentValue = selectedOptions[key] as string;
			if (value.selectableValues?.includes(currentValue)) {
				this.debug('Selected option already in selectable values', key, currentValue);
				continue;
			}

			if (value.selectableValues?.length === 1) {
				this.debug(
					'Selected option has only one selectable value, updating',
					key,
					'=>',
					value.selectableValues[0],
				);
				updatedSelectedOptions.push([key, value.selectableValues[0]]);
			} else {
				this.debug('Selected option has been updated', key);
				this.store.dispatch(reportItemError(new CartItemOptionsInvalidError(item.id, itemSource)));
				updatedSelectedOptions.push([key, undefined]);
			}
		}
		const newItem = {
			...item,
			options: {
				...item.options,
				selectedOptions: {
					...Object.fromEntries(updatedSelectedOptions),
					// Keep original selected options that are not updated.
					...pick(item.options?.selectedOptions ?? {}, selectedOptionsNotUpdated),
				},
			},
		};
		this.debug(
			'Merged selected options',
			item.options?.selectedOptions,
			'=>',
			newItem.options?.selectedOptions,
		);
		return newItem;
	}

	/**
	 * Validates stock levels for all cart items and reports or dismisses errors based on stock availability.
	 * This ensures real-time error display when stock levels change in admin.
	 * @param items Cart items to validate
	 * @returns Map of item IDs to their stock deltas (negative means insufficient stock)
	 */
	validateStockLevels(items: api.CartItemWithProductDto[]): Map<string, number> {
		this.debug('Validating stock levels for cart items');
		const stockDeltas = new Map<string, number>();

		for (const item of items) {
			// Skip non-stock products
			if (item.product?.type !== 'stock') continue;

			const quantity = getProductQuantity(item, 0);
			// Type assertion is safe here because full product data is always loaded before this method is called
			const stockDelta = getStockAmount(item.product as api.ProductDto, { quantity }, items, item);

			// Store the stock delta for return
			stockDeltas.set(item.id, stockDelta);

			if (stockDelta >= 0) {
				// Stock is sufficient, dismiss any existing stock errors
				this.store.dispatch(
					dismissItemError(
						new CartItemStockLevelTooLowError(item.id, ItemSource.ShoppingCart, stockDelta),
					),
				);
			} else {
				// Stock is insufficient, report error
				this.debug('Stock level too low for item', item.product.sku, {
					quantity,
					stockLevel: item.product.stock_level,
					stockDelta,
				});
				this.store.dispatch(
					reportItemError(
						new CartItemStockLevelTooLowError(item.id, ItemSource.ShoppingCart, stockDelta),
					),
				);
			}
		}

		return stockDeltas;
	}

	/**
	 * Processes file errors for the given cart items. Asynchronous due to the use of observables and
	 * the fact that we may have to wait for file api responses.
	 * @param items Cart items to process
	 * @param itemSource Source of the cart items
	 * @returns Promise that resolves when all cart items have been checked for potential file errors.
	 */
	private async processFileErrors(
		items: api.CartItemWithProductDto[],
		itemSource: ItemSource,
	): Promise<void[]> {
		const responses = [];
		for (const item of items) {
			const printfileKey = item.options.printfile?.key;
			if (!printfileKey) continue;

			const getFileRes = firstValueFrom(
				this.store.select(getFileByKey(printfileKey)).pipe(firstTruthy),
			).then((file) => {
				switch (file.antivirusState?.status) {
					case AntivirusStatus.INFECTED:
						this.store.dispatch(
							reportItemError(new CartItemAntivirusInfectedError(item.id, itemSource)),
						);
						break;
					case AntivirusStatus.ERROR:
						this.store.dispatch(
							reportItemError(new CartItemAntivirusScanFailedError(item.id, itemSource)),
						);
						break;
				}
				// Process preflight errors
				if (file.preflightState?.jobState?.error?.type === PreflightError.UNSUPPORTED_FILE_TYPE) {
					this.store.dispatch(
						reportItemError(new CartItemUnsupportedPrintfileError(item.id, itemSource)),
					);
				}
			});

			responses.push(getFileRes);
		}

		return await Promise.all(responses);
	}
}
